// The sport catalog (spec 006), testdata/sports.json's;
// sports.test.ts keeps them equal. The table's name is English, as the
// server and the watches read it; a sport's shown name comes from the
// catalog key named for its ID (spec 013 FR-005), as in the apps.
import { t } from '../i18n/index.ts';

export interface Sport {
  id: string;
  name: string;
  group: 'foot' | 'cycle' | 'water' | 'other';
  gps: boolean;
  pace: boolean;
  interval_ms: number;
  max_accuracy_m: number;
  max_speed_mps: number;
  met: number;
  icon: string;
  gpx: string;
  gpx_aliases: string[];
}

const TABLE: Sport[] = [
  { id: "run", name: "Run", group: "foot", gps: true, pace: true, interval_ms: 1000, max_accuracy_m: 20, max_speed_mps: 8, met: 9.8, icon: "running", gpx: "running", gpx_aliases: ["run", "street_running", "treadmill_running"] },
  { id: "trail_run", name: "Trail Run", group: "foot", gps: true, pace: true, interval_ms: 1000, max_accuracy_m: 20, max_speed_mps: 8, met: 9.0, icon: "mountain", gpx: "trail_running", gpx_aliases: ["trailrun", "trail_run"] },
  { id: "walk", name: "Walk", group: "foot", gps: true, pace: true, interval_ms: 2000, max_accuracy_m: 20, max_speed_mps: 8, met: 3.5, icon: "walking", gpx: "walking", gpx_aliases: ["walk"] },
  { id: "hike", name: "Hike", group: "foot", gps: true, pace: true, interval_ms: 2000, max_accuracy_m: 20, max_speed_mps: 8, met: 6.0, icon: "hiking", gpx: "hiking", gpx_aliases: ["hike"] },
  { id: "ride", name: "Ride", group: "cycle", gps: true, pace: false, interval_ms: 1000, max_accuracy_m: 30, max_speed_mps: 25, met: 7.5, icon: "biking", gpx: "cycling", gpx_aliases: ["biking", "ride", "road_biking", "road_cycling"] },
  { id: "mtb_ride", name: "Mountain Bike Ride", group: "cycle", gps: true, pace: false, interval_ms: 1000, max_accuracy_m: 30, max_speed_mps: 25, met: 8.5, icon: "biking", gpx: "mountain_biking", gpx_aliases: ["mountainbikeride", "mtb"] },
  { id: "ebike_ride", name: "E-Bike Ride", group: "cycle", gps: true, pace: false, interval_ms: 1000, max_accuracy_m: 30, max_speed_mps: 25, met: 4.0, icon: "biking", gpx: "e_biking", gpx_aliases: ["ebikeride", "e_bike_ride"] },
  { id: "open_water_swim", name: "Open Water Swim", group: "water", gps: true, pace: true, interval_ms: 1000, max_accuracy_m: 30, max_speed_mps: 3, met: 8.0, icon: "swimmer", gpx: "open_water_swimming", gpx_aliases: ["swim"] },
  { id: "pool_swim", name: "Pool Swim", group: "water", gps: false, pace: true, interval_ms: 1000, max_accuracy_m: 30, max_speed_mps: 3, met: 7.0, icon: "swimmer", gpx: "swimming", gpx_aliases: ["lap_swimming", "pool_swimming"] },
  { id: "paddle", name: "Paddle", group: "water", gps: true, pace: false, interval_ms: 1000, max_accuracy_m: 30, max_speed_mps: 8, met: 5.0, icon: "water", gpx: "paddling", gpx_aliases: ["kayaking", "canoeing", "stand_up_paddleboarding", "rowing"] },
  { id: "workout", name: "Workout", group: "other", gps: false, pace: false, interval_ms: 1000, max_accuracy_m: 20, max_speed_mps: 25, met: 5.0, icon: "heartbeat", gpx: "training", gpx_aliases: ["workout", "cardio", "hiit"] },
  { id: "weight_training", name: "Weight Training", group: "other", gps: false, pace: false, interval_ms: 1000, max_accuracy_m: 20, max_speed_mps: 25, met: 5.0, icon: "dumbbell", gpx: "strength_training", gpx_aliases: ["weighttraining", "weight_training"] },
  { id: "yoga", name: "Yoga", group: "other", gps: false, pace: false, interval_ms: 1000, max_accuracy_m: 20, max_speed_mps: 25, met: 2.5, icon: "spa", gpx: "yoga", gpx_aliases: [] },
  { id: "other", name: "Activity", group: "other", gps: true, pace: false, interval_ms: 1000, max_accuracy_m: 20, max_speed_mps: 25, met: 4.0, icon: "route", gpx: "other", gpx_aliases: ["generic"] },
];

// sportKey is the catalog key of a sport's name: "sport" and its ID in
// camel case, trail_run being sportTrailRun.
export function sportKey(id: string): string {
  return 'sport' + id.split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

// SPORTS are the table's sports, each name read from the catalog when shown,
// or the table's English when the catalog has none.
export const SPORTS: Sport[] = TABLE.map((s) => {
  const english = s.name;
  return Object.defineProperty({ ...s }, 'name', {
    enumerable: true,
    get: () => {
      const key = sportKey(s.id);
      const name = t(key);
      return name === key ? english : name;
    },
  });
});

const byId = new Map(SPORTS.map((s) => [s.id, s]));

// sportOf returns a sport, or the catch-all "other" for one this version does not know.
export function sportOf(id: string): Sport {
  return byId.get(id) ?? byId.get('other')!;
}

export const GROUPS: { id: Sport['group']; name: string }[] = (['foot', 'cycle', 'water', 'other'] as const).map((id) => ({
  id,
  get name() {
    return t('sportsGroup', { group: id });
  },
}));
